import React, { useState } from 'react';
import { Utensils, Wine, ChevronDown } from 'lucide-react';
import './Menu.css';

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState(() => {
    if (typeof window !== 'undefined') {
      const t = new URLSearchParams(window.location.search).get('menu');
      if (['american', 'indian', 'drinks'].includes(t)) return t;
    }
    return 'american';
  });
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const americanMenu = [
    {
      category: 'Appetizers',
      items: [
        { name: 'Whimsy Cheese Curds', price: 12, description: 'Lightly fried white cheddar cheese curds served with a sweet chili sauce.' },
        { name: 'Crispy Onion Rings', price: 10, description: 'Onion rings dipped in sweet ale and served with the choice of dipping sauce' },
        { name: 'Mozzarella Sticks', price: 12, description: 'Six creamy mozzarella sticks, deep-fried and served with ranch or marinara sauce' },
        { name: 'Hummus Plate', price: 13, description: 'Garlic hummus with assorted vegetables served with corn tortilla chips or pita bread' },
        { name: 'Twisted Knot Warm Pretzel', price: 12, description: 'A giant salted pretzel served with beer cheese' },
        { name: 'Southwest Egg Rolls', price: 12, description: 'Crispy rolls filled with spiced chicken, corn salsa, and southwestern flavors.' }
      ]
    },
    {
      category: 'Burgers',
      items: [
        { name: 'Juicy Black and Blue Burger', price: 16, description: 'Seasoned with Cajun seasonings, then crowned with crumbled blue cheese and Applewood bacon' },
        { name: 'House Cheeseburger', price: 13, description: 'Our ½ house burger and all of the fixings' },
        { name: 'Bacon Cheeseburger', price: 15, description: 'Crispy fried onion, lettuce, tomato, pickle, and melted Tillamook cheddar are topped with applewood smoked bacon' },
        { name: 'Memphis BBQ Burger', price: 15, description: 'Served on a pub bun with sautéed onions, bacon, lettuce, tomato, onion strings, and house BBQ sauce.' }
      ]
    },
    {
      category: 'Baskets',
      items: [
        { name: 'Chicken Strip Basket', price: 14, description: 'Delicious thin-cut breast pieces deep fried to perfection, served with your choice of side' },
        { name: 'Breaded Shrimp Basket', price: 13, description: 'Golden-fried shrimp served with fries and cocktail sauce.' },
        { name: 'Fish & Chips', price: 13, description: 'Icelandic beer battered cod fillets served with fresh lemon wedges and tartar sauce' },
        { name: 'Wings', price: 14, description: '6-piece Bone-in wings. Sauce: Sweet Chili, Garlic Parmesan, BBQ, Buffalo' }
      ]
    }
  ];

  const indianMenu = [
    {
      category: 'Chicken Specialities',
      items: [
        { name: 'Chicken Tikka Masala', price: 19, description: 'Tender boneless chicken marinated with various spices in a subtly spiced tomato-cream sauce, baked in the tandoor' },
        { name: 'Chicken Curry', price: 19, description: 'Chicken composed with curry sauce or gravy seasoned with a mixture of ground spices' },
        { name: 'Butter Chicken', price: 19, description: 'Butter and a tomato cream sauce enriched with herbs served over grilled marinated chicken.' },
        { name: 'Saag Chicken', price: 19, description: 'Tender chicken cooked in a creamy, spiced spinach sauce with traditional Indian herbs and seasonings.' },
        { name: 'Chicken Biryani', price: 19, description: 'Aromatic basmati rice slow-cooked with marinated chicken, warm spices, and fresh herbs for a rich and flavorful one-pot dish.' }
      ]
    },
    {
      category: 'Vegetarian Delights',
      items: [
        { name: 'Dal Makhani', price: 15, description: 'Creamy black lentils simmered with aromatic spices, offering a delightful blend of flavors and textures.' },
        { name: 'Shahi Paneer', price: 18, description: 'Paneer cubes in a creamy tomato gravy, infused with aromatic spices and finished with a touch of butter.' },
        { name: 'Palak Paneer', price: 17, description: 'A savory blend of tender paneer cheese and spinach in creamy sauce, infused with aromatic spices for a satisfying meal.' },
        { name: 'Chana Masala', price: 15, description: 'Chickpeas simmered in a tangy tomato-onion gravy with bold Indian spices and fresh herbs.' },
        { name: 'Samosas', price: 6, description: 'Two triangular pastries filled with spicy potatoes and peas, popular in South Asian cuisine.' }
      ]
    }
  ];

  const drinks = [
    {
      category: 'Vodka',
      items: [
        { name: 'Sea Breeze', price: 7, description: "Tito's, grapefruit, cranberry." },
        { name: 'Watermelon Greyhound', price: 7, description: 'Reyka Vodka, watermelon, grapefruit.' },
        { name: 'Adult Capri-Sun', price: 8, description: "Tito's, Disaronno, triple sec, sweet & sour, lemonade." },
        { name: 'Pink Starburst', price: 8, description: 'Vanilla vodka, watermelon pucker, sweet & sour, Sprite.' },
        { name: 'Raspberry Kamikaze', price: 8, description: 'Raspberry vodka, Razzmatazz/puree, sweet & sour, Sprite.' },
        { name: 'Electric Lemonade', price: 8, description: 'Blue raspberry vodka, sweet & sour, lemonade, Sprite.' },
        { name: 'Nectarine Cooler', price: 8, description: '44 North Nectarine Vodka, lime, peach, mint, soda water.' },
        { name: 'Watermelon Jolly Rancher', price: 9, description: 'Reyka, peach schnapps, watermelon pucker, sweet & sour, Sprite.' },
        { name: 'White Russian', price: 9, description: 'Crater Lake hazelnut vodka, Kahlua, cream.' },
        { name: 'Pineapple Upside Down', price: 9, description: 'Pineapple, whipped cream vodka, grenadine.' },
        { name: 'Mango Lassi Martini', price: 10, description: 'New Amsterdam Mango Vodka, mango, simple syrup, lemon juice.' }
      ]
    },
    {
      category: 'Rum',
      items: [
        { name: 'Malibu Barbie', price: 8, description: 'Malibu coconut + mango, pineapple, grenadine.' },
        { name: 'Guava Colada', price: 8, description: 'Guava Malibu, triple sec, coconut, lime, pineapple.' },
        { name: 'Rum Punch', price: 8, description: 'Flor de Cana, orange, cranberry, pineapple.' },
        { name: 'Classic Mojito', price: 8, description: 'Rum, lime, simple syrup, mint.' },
        { name: 'Hemingway Daiquiri', price: 9, description: 'Flor de Cana, grapefruit juice, maraschino cherries, lime juice.' }
      ]
    },
    {
      category: 'Tequila',
      items: [
        { name: 'Paloma', price: 7, description: 'Milagro, grapefruit juice, club soda, simple syrup, lime juice.' },
        { name: 'Raspberry Paloma', price: 7, description: 'Milagro, raspberry, triple sec, lime, grapefruit.' },
        { name: 'Desert Diablo', price: 8, description: 'Milagro, pineapple, lime, ginger beer.' },
        { name: 'Ranch Water', price: 9, description: 'Lalo Tequila, lime, soda. Ask about flavor options.' },
        { name: 'Blackberry Margarita', price: 9, description: 'Tequila, Cointreau, blackberry, lime, salt.' }
      ]
    },
    {
      category: 'Whiskey',
      items: [
        { name: 'Bourbon Stone Sour', price: 8, description: 'Four Roses whiskey, simple syrup, lemon juice, orange juice.' },
        { name: 'Poison Apple', price: 8, description: 'Crown Apple, apple pucker, cranberry.' },
        { name: 'Electric Ice Tea', price: 8, description: 'Four Roses, vodka, gin, triple sec, cola.' },
        { name: 'Kentucky Buck', price: 8, description: 'Penelope Four Grain, lemon juice, simple syrup, ginger beer, Angostura bitters, strawberry.' },
        { name: 'Manhattan', price: 9, description: 'Rye whiskey, sweet vermouth, Angostura bitters. Served in a martini glass.' },
        { name: 'Kentucky Peach Mule', price: 10, description: 'Crown Peach, lemon, lime, ginger beer.' },
        { name: 'French 95', price: 11, description: 'Four Roses Small Batch, Champagne, simple syrup, lemon juice, orange juice.' }
      ]
    },
    {
      category: 'Gin',
      items: [
        { name: 'Aviation', price: 6, description: 'Gin, lemon juice, maraschino cherries.' },
        { name: 'Curiousity Cooler', price: 8, description: 'Hendricks Gin, lime, simple syrup, soda.' },
        { name: 'Blue Train', price: 8, description: 'McQueen & Ultraviolet gin, triple sec, blue curacao, lemon juice.' },
        { name: 'Royal Spritz Tonic', price: 9, description: 'Hendricks Another, cranberry, lime, simple syrup, tonic.' },
        { name: 'Botanist 75', price: 10, description: 'The Botanist Gin, Cointreau, lemon, simple syrup, Champagne, orange bitters.' }
      ]
    },
    {
      category: 'Premium Tequila Selection',
      items: [
        { name: 'Don Julio 1942', description: 'Añejo tequila with rich caramel and chocolate notes' },
        { name: 'Patrón Silver', description: 'Crystal clear premium tequila' },
        { name: 'Clase Azul Reposado', description: 'Ultra-premium reposado with smooth vanilla finish' },
        { name: 'Casamigos Blanco', description: 'Smooth blanco with citrus and vanilla notes' }
      ]
    },
    {
      category: 'Beer & Wine',
      items: [
        { name: 'Craft Beer Selection', description: 'Rotating local and regional craft beers on tap' },
        { name: 'Domestic & Import', description: 'Premium bottles and draft selections' },
        { name: 'Wine by the Glass', description: 'Curated selection of red, white, and sparkling wines' }
      ]
    }
  ];

  const getCurrentMenu = () => {
    if (activeCategory === 'american') return americanMenu;
    if (activeCategory === 'indian') return indianMenu;
    return drinks;
  };

  return (
    <section id="menu" className="menu-section">
      <div className="menu-container">
        <div className="menu-header">
          <h2 className="section-title">Our Menu</h2>
          <p className="section-subtitle">Elevated classics. Authentic flavors. Premium spirits.</p>
        </div>

        <div className="menu-tabs">
          <button 
            className={`menu-tab ${activeCategory === 'american' ? 'active' : ''}`}
            onClick={() => { setActiveCategory('american'); setExpandedSection(null); }}
            data-testid="tab-american"
          >
            <Utensils size={18} />
            American
          </button>
          <button 
            className={`menu-tab ${activeCategory === 'indian' ? 'active' : ''}`}
            onClick={() => { setActiveCategory('indian'); setExpandedSection(null); }}
            data-testid="tab-indian"
          >
            <Utensils size={18} />
            Indian
          </button>
          <button 
            className={`menu-tab ${activeCategory === 'drinks' ? 'active' : ''}`}
            onClick={() => { setActiveCategory('drinks'); setExpandedSection(null); }}
            data-testid="tab-drinks"
          >
            <Wine size={18} />
            Drinks
          </button>
        </div>

        {activeCategory === 'indian' && (
          <div className="indian-note">
            <p>All Indian entrées served with Basmati rice. Add naan $4 | Garlic naan $5</p>
          </div>
        )}

        <div className="menu-accordion">
          {getCurrentMenu().map((section) => {
            const sectionId = `${activeCategory}-${section.category.toLowerCase().replace(/\s+/g, '-')}`;
            const isExpanded = expandedSection === sectionId;
            
            return (
              <div key={sectionId} className="accordion-section" data-testid={`section-${sectionId}`}>
                <button 
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleSection(sectionId)}
                  aria-expanded={isExpanded}
                >
                  <span className="accordion-title">{section.category}</span>
                  <span className="accordion-meta">
                    <span className="item-count">{section.items.length} items</span>
                    <ChevronDown size={20} className={`accordion-icon ${isExpanded ? 'rotated' : ''}`} />
                  </span>
                </button>
                
                <div className={`accordion-content ${isExpanded ? 'expanded' : ''}`}>
                  <div className="menu-items-grid">
                    {section.items.map((item) => (
                      <div key={`${section.category}-${item.name}`} className="menu-item">
                        <div className="item-header">
                          <h4 className="item-name">{item.name}</h4>
                          {item.price && <span className="item-price">${item.price}</span>}
                        </div>
                        <p className="item-description">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Menu;
